import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { z } from 'zod';
import { usePasswordStrengthValidator } from '@/entities/users';
import { UserFormValues } from './types';

const createSchema = (
  t: TFunction<readonly ['users', 'common']>,
  checkPasswordStrength: ReturnType<typeof usePasswordStrengthValidator>,
  isCreating: boolean,
) => {
  const baseSchema = z.object({
    firstName: z
      .string()
      .min(1, t('common:required'))
      .max(100, t('common:maxLength', { count: 100 })),
    lastName: z
      .string()
      .min(1, t('common:required'))
      .max(100, t('common:maxLength', { count: 100 })),
    middleName: z
      .string()
      .max(100, t('common:maxLength', { count: 100 }))
      .optional()
      .or(z.literal('')),
    email: z.string().min(1, t('common:required')),
    roleCode: z.string().min(1, t('common:required')),
    isActive: z.boolean(),
    // Схема разбора задаёт и то, что доедет до сабмита: поля состава, не описанные здесь, будут отброшены
    userGroups: z.array(z.object({ id: z.number(), name: z.string() })),
  }) satisfies z.ZodType<Omit<UserFormValues, 'password'>>;

  if (isCreating) {
    return (
      baseSchema
        .extend({ password: z.string().min(1, t('common:required')) })
        // Владелец пароля — заводимый пользователь: его email и ФИО берутся из этой же карточки
        .superRefine(({ password, email, firstName, lastName, middleName }, ctx) => {
          const result = password ? checkPasswordStrength(password, { email, firstName, lastName, middleName }) : true;
          if (result !== true) {
            ctx.addIssue({ code: 'custom', path: ['password'], message: result });
          }
        }) satisfies z.ZodType<UserFormValues>
    );
  }

  return baseSchema satisfies z.ZodType<Omit<UserFormValues, 'password'>>;
};

export const useUserSchema = (isCreating: boolean) => {
  const { t } = useTranslation(['users', 'common']);
  const checkPasswordStrength = usePasswordStrengthValidator();

  return createSchema(t, checkPasswordStrength, isCreating);
};
